import type { ReactNode } from 'react'
import { AlertTriangle, Store } from 'lucide-react'
import { useProductOptions } from '@/api/hooks'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatMoney } from '@/lib/utils'
import type { components } from '@/api/schema'

type PickerLink = components['schemas']['ProductPickerLinkRead']
type PickerSwitch = components['schemas']['ProductPickerSwitchRead']
type PickerOption = components['schemas']['ProductPickerOptionRead']

const SOURCE_LABELS: Record<PickerSwitch['source'], string> = {
  webstaurant: 'from Webstaurant',
  model: 'from model number',
  documents: 'from spec sheets',
}

const PILL =
  'inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs leading-5'
const PILL_SELECTED = 'border-primary/40 bg-primary/10 font-medium text-primary'

function Shell({ children, ...props }: { children: ReactNode } & Record<`data-${string}`, string>) {
  return (
    <section
      aria-labelledby="product-options-title"
      className="@container/product-options overflow-hidden rounded-xl border bg-card"
      {...props}
    >
      <div className="border-b px-4 py-3">
        <h2 id="product-options-title" className="text-sm font-semibold">
          Product options
        </h2>
      </div>
      {children}
    </section>
  )
}

/**
 * Another catalog product, embedded like every other product reference in the dashboard: photo,
 * model link, title, manufacturer and store price. `label` is the option it stands for; the
 * current product is marked and not linked.
 */
function ProductOptionProduct({
  product,
  label,
  current = false,
}: {
  product: PickerLink
  label?: ReactNode
  current?: boolean
}) {
  // The fallback title is just manufacturer + model, which the cell already shows.
  const title =
    product.title && product.title !== `${product.manufacturer_name ?? ''} ${product.model}`.trim()
      ? product.title
      : null
  return (
    <li
      data-product-option-product={product.product_id}
      aria-current={current ? 'true' : undefined}
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1',
        // The current product is marked by its tint alone (USA-1392: no self-evident labels).
        current && '-mx-2 rounded-lg bg-muted/60 px-2 py-1',
        label !== undefined && '@4xl/product-options:grid-cols-[11rem_minmax(0,1fr)_auto]',
      )}
    >
      {label !== undefined && (
        <div className="col-span-2 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs font-medium break-words @4xl/product-options:col-span-1 @4xl/product-options:pt-0.5">
          {label}
        </div>
      )}
      <POLineItemProductCell
        compact
        model={product.model}
        description={title}
        imageUrl={product.image_url}
        productUuid={current ? null : product.product_uuid}
        manufacturerName={product.manufacturer_name}
        manufacturerUuid={product.manufacturer_uuid}
      />
      {product.store_price != null && (
        <span className="pt-0.5 text-right text-sm tabular-nums">
          {formatMoney(product.store_price)}
        </span>
      )}
    </li>
  )
}

function ProductOptionProducts({ children }: { children: ReactNode }) {
  return <ul className="w-full min-w-0 space-y-3">{children}</ul>
}

function StorefrontBadge() {
  return (
    <Badge variant="secondary" className="font-normal">
      <Store aria-hidden="true" />
      On product page
    </Badge>
  )
}

function OptionCode({ code }: { code: string }) {
  return <span className="font-mono text-[11px] text-muted-foreground">{code}</span>
}

function DefaultMark() {
  return <span className="text-[11px] font-normal text-muted-foreground">Default</span>
}

/** Options made on this product read as pills; an option that adds another product shows it. */
function ChoiceOptions({ options }: { options: PickerOption[] }) {
  const pills = options.filter((option) => !option.linked_product)
  const products = options.filter((option) => option.linked_product)
  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      {pills.length > 0 && (
        <div className="flex min-w-0 flex-wrap items-start gap-1.5">
          {pills.map((option) => (
            <span key={option.id} className={cn(PILL, option.is_default && PILL_SELECTED)}>
              {option.label}
              {option.manufacturer_option_id && <OptionCode code={option.manufacturer_option_id} />}
              {option.is_default && <DefaultMark />}
            </span>
          ))}
        </div>
      )}
      {products.length > 0 && (
        <ProductOptionProducts>
          {products.map(
            (option) =>
              option.linked_product && (
                <ProductOptionProduct
                  key={option.id}
                  product={option.linked_product}
                  label={
                    <>
                      <span>{option.label}</span>
                      {option.manufacturer_option_id && (
                        <OptionCode code={option.manufacturer_option_id} />
                      )}
                      {option.is_default && <DefaultMark />}
                    </>
                  }
                />
              ),
          )}
        </ProductOptionProducts>
      )}
    </div>
  )
}

function OptionRow({
  name,
  meta,
  children,
}: {
  name: string
  meta: ReactNode
  children: ReactNode
}) {
  return (
    <li className="grid gap-x-4 gap-y-1.5 px-4 py-2.5 @2xl/product-options:grid-cols-[13rem_minmax(0,1fr)]">
      <div className="min-w-0">
        <p className="text-sm font-medium break-words">{name}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 empty:hidden">{meta}</div>
      </div>
      <div className="flex min-w-0 flex-wrap items-start gap-1.5">{children}</div>
    </li>
  )
}

function OptionBlock({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <div aria-labelledby={id} role="group">
      <h3
        id={id}
        className="bg-muted/40 px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase"
      >
        {title}
      </h3>
      <ul className="divide-y">{children}</ul>
    </div>
  )
}

/** The cleaned product options shoppers and Quote Builder offer, above the raw AI evidence. */
export function ProductOptionsSection({ productId }: { productId: string }) {
  const { data, isLoading, isError } = useProductOptions(productId)

  if (isLoading) {
    return (
      <section
        data-product-options-loading="true"
        role="status"
        aria-label="Loading product options"
        className="space-y-3 rounded-xl border bg-card px-4 py-3"
      >
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </section>
    )
  }

  if (isError) {
    return (
      <section
        data-product-options-error="true"
        role="alert"
        className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-card px-4 py-3"
      >
        <AlertTriangle className="size-4 shrink-0 text-destructive" aria-hidden="true" />
        <p className="text-sm font-medium">Product options could not be loaded</p>
      </section>
    )
  }

  const hasOptions =
    !!data &&
    (data.switches.length > 0 || data.choices.length > 0 || data.accessory_groups.length > 0)

  if (!data || !hasOptions) {
    return (
      <Shell data-product-options-empty="true">
        <p className="px-4 py-3 text-sm text-muted-foreground">No product options</p>
      </Shell>
    )
  }

  return (
    <Shell data-product-options="true">
      <div className="divide-y">
        {data.switches.length > 0 && (
          <OptionBlock id="product-options-versions" title="Versions">
            {data.switches.map((sw) => (
              <OptionRow
                key={sw.id}
                name={sw.name}
                meta={
                  <>
                    {sw.storefront && <StorefrontBadge />}
                    <span className="text-xs text-muted-foreground">{SOURCE_LABELS[sw.source]}</span>
                  </>
                }
              >
                <ProductOptionProducts>
                  {sw.values.map((value) => (
                    <ProductOptionProduct
                      key={`${value.label}-${value.product.product_id}`}
                      product={value.product}
                      label={value.label}
                      current={value.is_current}
                    />
                  ))}
                </ProductOptionProducts>
              </OptionRow>
            ))}
          </OptionBlock>
        )}

        {data.choices.length > 0 && (
          <OptionBlock id="product-options-choices" title="Choices">
            {data.choices.map((choice) => (
              <OptionRow
                key={choice.id}
                name={choice.name}
                meta={
                  <>
                    {choice.required && (
                      <Badge variant="outline" className="font-normal">
                        Required
                      </Badge>
                    )}
                    {choice.storefront && <StorefrontBadge />}
                  </>
                }
              >
                <ChoiceOptions options={choice.options} />
              </OptionRow>
            ))}
          </OptionBlock>
        )}

        {data.accessory_groups.length > 0 && (
          <OptionBlock id="product-options-add-ons" title="Add-ons">
            {data.accessory_groups.map((group) => (
              <OptionRow
                key={group.id}
                name={group.name}
                meta={group.storefront && <StorefrontBadge />}
              >
                <ProductOptionProducts>
                  {group.products.map((product) => (
                    <ProductOptionProduct key={product.product_id} product={product} />
                  ))}
                </ProductOptionProducts>
              </OptionRow>
            ))}
          </OptionBlock>
        )}

        {data.more_options && (
          <p className="px-4 py-2.5 text-xs text-muted-foreground">
            Product page also shows: Need something else? Contact us.
          </p>
        )}
      </div>
    </Shell>
  )
}
